// src/routes/requests/components/RequestsTableServerPaginated.tsx
import React, { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import {
  Table,
  Badge,
  Group,
  ActionIcon,
  Text,
  Box,
  Pagination,
  Tooltip,
  LoadingOverlay,
  ScrollArea,
} from '@mantine/core';
import { IconEye, IconEdit, IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import { PageSizeSelector } from '../../../components/ui/PageSizeSelector';
import { Request } from '../../../types/api';
import { ColumnResizer } from '../../../components/data-table/ColumnResizer';
import { RESIZABLE_TABLE_OPTIONS, useColumnSizing } from '../../../hooks/use-column-sizing';

interface RequestsTableServerPaginatedProps {
  tableId?: string;
  data: Request[];
  onView: (id: number | string) => void;
  onEdit: (id: number | string) => void;
  loading?: boolean;
  fetching?: boolean;
  totalCount: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const getStatusBadge = (status: number) => {
  const statusMap: { [key: number]: { label: string; color: string } } = {
    1: { label: 'Nuova', color: 'blue' },
    2: { label: 'In Lavorazione', color: 'cyan' },
    3: { label: 'In Attesa di Completamento', color: 'yellow' },
    4: { label: 'Completata', color: 'green' },
    5: { label: 'Annullata', color: 'red' },
    6: { label: 'Sospesa', color: 'gray' },
    7: { label: 'Pre-Campionatura', color: 'grape' },
    8: { label: 'Pre-Campionatura Completata', color: 'indigo' }
  };
  const statusInfo = statusMap[status] || { label: 'Sconosciuto', color: 'gray' };
  return <Badge variant="filled" color={statusInfo.color} autoContrast={false} styles={{ label: { color: 'white' } }}>{statusInfo.label}</Badge>;
};

export function RequestsTableServerPaginated({
  tableId = 'requests',
  data,
  onView,
  onEdit,
  loading = false,
  fetching = false,
  totalCount,
  totalPages,
  pageNumber,
  pageSize,
  onPageChange,
  onPageSizeChange
}: RequestsTableServerPaginatedProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const { columnSizing, onColumnSizingChange } = useColumnSizing(tableId);

  const columns = useMemo<ColumnDef<Request>[]>(
    () => [
      {
        id: 'progressiveNumber',
        accessorFn: (row) => row.progressiveNumber ? `${row.progressiveNumber}/${row.progressiveYear}` : null,
        header: 'N. Progr.',
        size: 100,
        cell: ({ row }) => {
          const num = row.original.progressiveNumber;
          const year = row.original.progressiveYear;
          if (!num) return <Text c="dimmed">-</Text>;
          return (
            <Text fw={600} ff="monospace" size="sm">
              {num}/{year}
            </Text>
          );
        },
        sortingFn: (rowA, rowB) => {
          const aNum = rowA.original.progressiveNumber || 0;
          const aYear = rowA.original.progressiveYear || 0;
          const bNum = rowB.original.progressiveNumber || 0;
          const bYear = rowB.original.progressiveYear || 0;
          // Sort by year first, then by number
          if (aYear !== bYear) return aYear - bYear;
          return aNum - bNum;
        },
      },
      {
        accessorKey: 'title',
        header: 'Titolo',
        size: 300,
        cell: ({ row }) => {
          const title = row.original.title || '';
          const notes = row.original.notes || '';
          return (
            <Box>
              <Tooltip label={title} multiline w={350} withArrow disabled={!title || title.length < 40}>
                <Text fw={500} lineClamp={1}>{title}</Text>
              </Tooltip>
              <Tooltip label={notes} multiline w={350} withArrow disabled={!notes || notes.length < 50}>
                <Text size="sm" c="dimmed" lineClamp={1}>
                  {notes?.substring(0, 50)}{notes && notes.length > 50 ? '...' : ''}
                </Text>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        accessorKey: 'clientName',
        header: 'Cliente',
        size: 180,
        cell: ({ getValue }) => <Text>{getValue() as string || '-'}</Text>,
      },
      {
        accessorKey: 'createdByEmail',
        header: 'Inserita da',
        size: 220,
        cell: ({ getValue }) => <Text size="sm">{getValue() as string || '-'}</Text>,
      },
      {
        accessorKey: 'status',
        header: 'Stato',
        size: 200,
        cell: ({ getValue }) => getStatusBadge(getValue() as number),
      },
      {
        accessorKey: 'completedAt',
        header: 'Data Chiusura',
        size: 130,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          return value
            ? <Text size="sm">{new Date(value).toLocaleDateString('it-IT')}</Text>
            : <Text size="sm" c="dimmed">-</Text>;
        },
        sortingFn: 'datetime',
      },
      {
        accessorKey: 'createdAt',
        header: 'Data Creazione',
        size: 130,
        cell: ({ getValue }) => new Date(getValue() as string).toLocaleDateString('it-IT'),
        sortingFn: 'datetime',
      },
      {
        id: 'actions',
        header: 'Azioni',
        size: 90,
        enableSorting: false,
        enableResizing: false,
        cell: ({ row }) => (
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              color="blue"
              onClick={(e) => { e.stopPropagation(); onView(row.original.id); }}
              title="Visualizza"
            >
              <IconEye size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="yellow"
              onClick={(e) => { e.stopPropagation(); onEdit(row.original.id); }}
              title="Modifica"
            >
              <IconEdit size={16} />
            </ActionIcon>
          </Group>
        ),
      },
    ],
    [onView, onEdit]
  );

  const table = useReactTable({
    data,
    columns,
    ...RESIZABLE_TABLE_OPTIONS,
    state: { sorting, columnSizing },
    onSortingChange: setSorting,
    onColumnSizingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  if (loading) {
    return <Text c="dimmed" ta="center" py="xl">Caricamento...</Text>;
  }

  if (data.length === 0) {
    return <Text c="dimmed" ta="center" py="xl">Nessuna richiesta trovata</Text>;
  }

  const startRow = (pageNumber - 1) * pageSize + 1;
  const endRow = Math.min(pageNumber * pageSize, totalCount);

  return (
    <Box pos="relative">
      <LoadingOverlay visible={fetching} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />

      <ScrollArea type="auto">
      <Table striped highlightOnHover layout="fixed" style={{ width: table.getTotalSize(), minWidth: '100%' }}>
        <Table.Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sortDirection = header.column.getIsSorted();

                return (
                  <Table.Th
                    key={header.id}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    style={{
                      cursor: canSort ? 'pointer' : 'default',
                      userSelect: canSort ? 'none' : 'auto',
                      width: header.getSize(),
                      position: 'relative',
                    }}
                  >
                    <Group gap={4} wrap="nowrap">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && (
                        <Box style={{ width: 16, display: 'flex', alignItems: 'center' }}>
                          {sortDirection === 'asc' && <IconChevronUp size={14} />}
                          {sortDirection === 'desc' && <IconChevronDown size={14} />}
                        </Box>
                      )}
                    </Group>
                    <ColumnResizer header={header} />
                  </Table.Th>
                );
              })}
            </Table.Tr>
          ))}
        </Table.Thead>
        <Table.Tbody>
          {table.getRowModel().rows.map((row) => (
            <Table.Tr key={row.id} onClick={() => onView(row.original.id)} style={{ cursor: 'pointer' }}>
              {row.getVisibleCells().map((cell) => (
                <Table.Td key={cell.id} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      </ScrollArea>

      <Group justify="space-between" p="md" wrap="wrap" gap="sm">
        <Group gap="lg">
          <Text size="sm" c="dimmed">
            Visualizzati {startRow}-{endRow} di {totalCount}
          </Text>
          <PageSizeSelector
            value={pageSize}
            onChange={onPageSizeChange}
          />
        </Group>
        {totalPages > 1 && (
          <Pagination
            total={totalPages}
            value={pageNumber}
            onChange={onPageChange}
          />
        )}
      </Group>
    </Box>
  );
}
